import React, { useState } from "react";
import { Loader2, LocateFixed, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { COUNTRY_CODES, countryLabel } from "@/lib/countries";
import { getCurrentPosition, isLocationDenied } from "@/lib/geo";

const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, v), s);
const fmtKm = (km) => (km < 1 ? "< 1" : km < 10 ? String(Math.round(km * 10) / 10) : String(Math.round(km)));

// Lot 659 — « Ville détectée » : un tap → position du téléphone (envoyée seulement à /geo/nearest, jamais enregistrée)
// → ville la plus proche + 2 alternatives → onPick({ country, city }) remplit pays + ville.
export const NearestCity = ({ onPick, testId = "nearest-city", className = "" }) => {
  const { t, lang } = useApp();
  const [state, setState] = useState("idle"); // idle | locating | ready | error
  const [cities, setCities] = useState([]);
  const [error, setError] = useState("");

  const locate = async () => {
    setState("locating");
    setError("");
    try {
      const pos = await getCurrentPosition({ timeout: 9000, enableHighAccuracy: false });
      const { data } = await api.get("/geo/nearest", { params: { lat: pos.lat, lon: pos.lon, limit: 5 } });
      // seuls les pays proposés dans le sélecteur sont retenus (sinon le pays ne s'afficherait pas)
      const list = (data?.cities || []).filter((c) => COUNTRY_CODES.includes(c.country)).slice(0, 3);
      if (!list.length) { setError(t("geo.none_near", "Aucune ville connue près de toi — tape le nom de la tienne.")); setState("error"); return; }
      setCities(list);
      setState("ready");
    } catch (e) {
      setError(isLocationDenied(e)
        ? t("geo.denied", "Localisation refusée — tu peux taper ta ville ci-dessus.")
        : t("geo.unavailable", "Position introuvable pour le moment. Réessaie ou tape ta ville."));
      setState("error");
    }
  };

  const pick = (c) => {
    onPick?.({ country: c.country, city: c.name });
    toast.success(t("geo.picked", "Pays et ville remplis ✓"));
    setState("idle");
    setCities([]);
  };

  const [best, ...others] = cities;
  return (
    <div className={`space-y-1.5 ${className}`} data-testid={testId} data-state={state}>
      {state !== "ready" && (
        <button type="button" onClick={locate} disabled={state === "locating"}
          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-[color:var(--yalola-surface-alt)] disabled:opacity-60"
          style={{ borderColor: "var(--yalola-primary)", color: "var(--yalola-primary)", background: "transparent" }} data-testid={`${testId}-btn`}>
          {state === "locating" ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
          {state === "locating" ? t("geo.locating", "Recherche de ta ville…") : t("geo.use_position", "Utiliser ma position")}
        </button>
      )}
      {state === "idle" && <p className="text-[11px] leading-snug" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-hint`}>{t("geo.privacy_hint", "Ta position n'est pas enregistrée : elle sert seulement à trouver ta ville.")}</p>}
      {state === "error" && <p className="text-[11px] leading-snug" style={{ color: "#B91C1C" }} data-testid={`${testId}-error`}>{error}</p>}
      {state === "ready" && best && (
        <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid={`${testId}-result`} data-city={best.name} data-country={best.country}>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="shrink-0" style={{ color: "var(--yalola-primary)" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{fill(t("geo.near_you", "Tu es près de {city}"), { city: best.name })}</div>
              <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>
                {countryLabel(best.country, lang)}{best.region && best.region !== best.name ? ` · ${best.region}` : ""} · {fill(t("geo.distance_km", "{n} km"), { n: fmtKm(best.distance_km) })}
              </div>
            </div>
            <button type="button" onClick={() => pick(best)} className="pill-primary rounded-full px-3 py-1.5 text-xs font-semibold shrink-0" data-testid={`${testId}-use`}>
              {t("geo.use_city", "Utiliser")}
            </button>
          </div>
          {others.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("geo.other_nearby", "Autres villes proches")} :</span>
              {others.map((c, i) => (
                <button key={`${c.country}-${c.name}`} type="button" onClick={() => pick(c)} className="chip text-[11px]" data-testid={`${testId}-alt-${i}`} data-city={c.name} data-country={c.country}>
                  {c.name} · {fill(t("geo.distance_km", "{n} km"), { n: fmtKm(c.distance_km) })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
